import React, { useEffect, useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import TaxIDForm from '@/components/TaxIDForm';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Wallet, FileText, ArrowRight, Globe, DollarSign, Building, ChevronDown } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

// Animation component for text appearing line by line
const AnimatedParagraph = ({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <p
      className={`text-lg text-muted-foreground mb-6 leading-relaxed transition-all duration-1000 ease-out ${
        isVisible
          ? 'opacity-100 transform translate-y-0'
          : 'opacity-0 transform translate-y-4'
      }`}
    >
      {children}
    </p>
  );
};

const TaxIDPage = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  return (
    <div className="min-h-screen">
      <Header />
      <SEO
        title="Obtain U.S. Tax ID Number (ITIN) | Freeman & Crown Law"
        description="Get your U.S. Tax ID Number (ITIN) to open bank accounts, file taxes, and operate financially in the U.S. from anywhere in the world."
        image="/lovable-uploads/f8f9f48d-77d0-4885-9af1-64dd5447bcf5.png"
        url={window.location.origin + '/services/tax-id'}
        type="website"
      />
      
      <main className="pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gold/10 rounded-full mb-6 transition-transform duration-300 hover:scale-110">
              <FileText className="h-8 w-8 text-gold-light transition-colors duration-300 hover:text-gold" />
            </div>
            <h1 className="font-rockwell text-4xl md:text-5xl font-semibold text-primary mb-6">
              Obtain Your U.S. Tax ID Number (ITIN)
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed">
              Access the U.S. financial system from anywhere in the world with your Individual Taxpayer Identification Number
            </p>
          </div>

          <div className="space-y-8 mb-16">
            <AnimatedParagraph delay={200}>
              You can use your new Tax ID Number (ITIN) to open U.S. bank accounts, file tax returns, apply for U.S. credit cards, open a PayPal account, open a Stripe account, and operate financially within the United States without needing to physically be in the United States.
            </AnimatedParagraph>

            <AnimatedParagraph delay={400}>
              We help our clients get their ITIN to comply with IRS tax ID reporting requirements without stress from anywhere in the world. Our streamlined process eliminates the complexity and confusion typically associated with ITIN applications.
            </AnimatedParagraph>
          </div>

          {/* Our Process Section */}
          <div className="space-y-8 mb-16">
            <div className="text-center">
              <h2 className="font-rockwell text-3xl md:text-4xl font-semibold text-primary mb-6">
                Our Streamlined ITIN Process
              </h2>
              <AnimatedParagraph delay={600}>
                We make obtaining your ITIN simple and stress-free with our expert team and proven process.
              </AnimatedParagraph>
            </div>

            <div className="grid md:grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="bg-gradient-to-br from-primary/5 to-gold/10 rounded-xl p-8 border border-gold/20 text-center transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-gold/20">`
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-primary">1</span>
                </div>
                <h3 className="font-rockwell text-xl font-semibold text-primary mb-4">
                  Personal ITIN Specialist Assignment
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  We assign you a dedicated ITIN specialist who will guide you through every step of the process and review your application with expert eyes to minimize the chance of rejection.
                </p>
              </div>

              <div className="bg-gradient-to-br from-primary/5 to-gold/10 rounded-xl p-8 border border-gold/20 text-center transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-gold/20">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-primary">2</span>
                </div>
                <h3 className="font-rockwell text-xl font-semibold text-primary mb-4">
                  ID Certification by CAAs
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  No need to mail your passport. We have Certified Acceptance Agents (CAAs) directly on our team who are certified and trained by the IRS to certify your identification documents ourselves.
                </p>
              </div>

              <div className="bg-gradient-to-br from-primary/5 to-gold/10 rounded-xl p-8 border border-gold/20 text-center transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-gold/20">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-primary">3</span>
                </div>
                <h3 className="font-rockwell text-xl font-semibold text-primary mb-4">
                  Complete Paperwork Handling
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Save time by letting us complete your paperwork for you. Our 24/7 customer service ensures your application gets processed quickly and efficiently.
                </p>
              </div>
            </div>
          </div>

          {/* Benefits Grid */}
          <div className="text-center mb-8">
            <h2 className="font-rockwell text-3xl md:text-4xl font-semibold text-primary mb-6">
              What You Can Do With Your ITIN
            </h2>
            <AnimatedParagraph delay={800}>
              Once you have your ITIN, you'll have access to the full U.S. financial system and can operate financially within the United States.
            </AnimatedParagraph>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            <div className="bg-gradient-to-br from-primary/5 to-gold/10 rounded-xl p-6 border border-gold/20 hover:border-gold/40 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-gold/20">
              <Building className="h-12 w-12 text-primary mb-4 transition-transform duration-300 hover:scale-110 hover:text-gold-light" />
              <h3 className="font-rockwell text-xl font-semibold text-primary mb-3">
                Open U.S. Bank Accounts
              </h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Access major U.S. banking institutions and establish your financial presence in America.
              </p>
            </div>

            <div className="bg-gradient-to-br from-primary/5 to-gold/10 rounded-xl p-6 border border-gold/20 hover:border-gold/40 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-gold/20">
              <FileText className="h-12 w-12 text-primary mb-4 transition-transform duration-300 hover:scale-110 hover:text-gold-light" />
              <h3 className="font-rockwell text-xl font-semibold text-primary mb-3">
                File Tax Returns
              </h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Comply with IRS requirements and file your U.S. tax returns properly and legally.
              </p>
            </div>

            <div className="bg-gradient-to-br from-primary/5 to-gold/10 rounded-xl p-6 border border-gold/20 hover:border-gold/40 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-gold/20">
              <Wallet className="h-12 w-12 text-primary mb-4 transition-transform duration-300 hover:scale-110 hover:text-gold-light" />
              <h3 className="font-rockwell text-xl font-semibold text-primary mb-3">
                Apply for Credit Cards
              </h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Build your U.S. credit history and access American credit card products.
              </p>
            </div>

            <div className="bg-gradient-to-br from-primary/5 to-gold/10 rounded-xl p-6 border border-gold/20 hover:border-gold/40 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-gold/20">
              <DollarSign className="h-12 w-12 text-primary mb-4 transition-transform duration-300 hover:scale-110 hover:text-gold-light" />
              <h3 className="font-rockwell text-xl font-semibold text-primary mb-3">
                PayPal & Stripe Accounts
              </h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Access digital payment platforms essential for online business operations.
              </p>
            </div>

            <div className="bg-gradient-to-br from-primary/5 to-gold/10 rounded-xl p-6 border border-gold/20 hover:border-gold/40 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-gold/20">
              <Globe className="h-12 w-12 text-primary mb-4 transition-transform duration-300 hover:scale-110 hover:text-gold-light" />
              <h3 className="font-rockwell text-xl font-semibold text-primary mb-3">
                Global Access
              </h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Operate financially in the U.S. without needing to physically be present.
              </p>
            </div>

            <div className="bg-gradient-to-br from-primary/5 to-gold/10 rounded-xl p-6 border border-gold/20 hover:border-gold/40 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-gold/20">
              <Building className="h-12 w-12 text-primary mb-4 transition-transform duration-300 hover:scale-110 hover:text-gold-light" />
              <h3 className="font-rockwell text-xl font-semibold text-primary mb-3">
                IRS Compliance
              </h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Meet all IRS tax ID reporting requirements with professional guidance.
              </p>
            </div>
          </div>


          {/* Call to Action */}
          <div className="bg-gradient-to-br from-primary/10 to-gold/20 rounded-2xl p-8 md:p-12 text-center border border-gold/30">
            <h2 className="font-rockwell text-3xl md:text-4xl font-semibold text-primary mb-6">
              Ready to Get Your ITIN?
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto leading-relaxed">
              Start your journey to U.S. financial access today. Our expert team will guide you through every step of the ITIN application process.
            </p>
            
            <Collapsible open={isFormOpen} onOpenChange={setIsFormOpen}>
              <CollapsibleTrigger asChild>
                <div className="flex justify-center">
                  <Button 
                    size="lg"
                    className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-3 text-lg"
                  >
                    Get Started
                    <ChevronDown className={`ml-2 h-5 w-5 transition-transform ${isFormOpen ? 'rotate-180' : ''}`} />
                  </Button>
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-8">
                <TaxIDForm onSuccess={() => setIsFormOpen(false)} />
              </CollapsibleContent>
            </Collapsible>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default TaxIDPage;